import type { Lesson } from '@yuan-math/curriculum';
import { insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * Grade 8 number.
 *
 * The exponent laws are usually taught as five rules to memorise, which is why
 * they are usually forgotten. Every one of them is repeated multiplication
 * counted carefully, and these lessons derive rather than assert — including
 * the two that look arbitrary, x⁰ = 1 and x⁻ⁿ = 1/xⁿ, which are forced by the
 * pattern rather than chosen.
 */
export const EXPONENT_RULE_LESSONS: Lesson[] = [
  lesson({
    id: 'exponent-rules-01',
    unitId: 'unit-exponent-laws',
    conceptId: 'exponent-rules',
    title: 'Laws of Exponents',
    summary: 'The rules for combining powers, every one of which is repeated multiplication counted carefully.',
    objectives: [
      'Multiply and divide powers of the same base',
      'Raise a power to another power',
      'Explain why a zero exponent gives 1 and a negative exponent gives a reciprocal',
    ],
    estimatedMinutes: 35,
    sections: {
      introduction: {
        title: 'Counting, not memorising',
        blocks: [
          text('There are five laws of exponents, and you are about to learn all of them without memorising any. Here is the only idea you need:'),
          math('x^{5} = x \\times x \\times x \\times x \\times x'),
          text('An exponent counts factors. Every rule below is what happens when you count them properly, which is why you can always rebuild a rule you have forgotten.'),
        ],
      },
      intuition: {
        title: 'Deriving all five',
        blocks: [
          text('**Multiplying.** Three xs, then four more xs. How many altogether?'),
          math('x^{3} \\times x^{4} = \\underbrace{(x\\,x\\,x)}_{3}\\underbrace{(x\\,x\\,x\\,x)}_{4} = x^{7}'),
          insight('So you *add* the exponents. Not because a rule says so, but because three things and four things is seven things.'),
          warning('The base does not change. 2³ × 2⁴ is 2⁷, never 4⁷ — you are counting 2s, not multiplying the 2s together.', 'What stays put'),
          text('**Dividing.** Matching factors cancel, one from the top with one from the bottom:'),
          math('\\frac{x^{7}}{x^{4}} = \\frac{x\\,x\\,x\\,x\\,x\\,x\\,x}{x\\,x\\,x\\,x} = x^{3}'),
          text('**A power of a power.** Four copies of x³, multiplied together:'),
          math('\\left(x^{3}\\right)^{4} = x^{3} x^{3} x^{3} x^{3} = x^{12}'),
          insight('Four lots of three is twelve, so here you *multiply*. This is the pair students most often swap: a product of powers adds, a power of a power multiplies.'),
          text('**Zero.** Now for the one that looks like a convention. Follow the pattern down, dividing by 2 each time:'),
          table(['Power', 'Value', 'Step'], [
            ['2⁴', '16', ''],
            ['2³', '8', '÷ 2'],
            ['2²', '4', '÷ 2'],
            ['2¹', '2', '÷ 2'],
            ['2⁰', '1', '÷ 2'],
          ]),
          text('The pattern does not permit 0. Halving 2 gives 1, so 2⁰ = 1 — and that is not a decision anyone made, it is what the arithmetic forces.'),
          text('**Negative.** Keep going down the same table:'),
          math('2^{-1} = \\tfrac{1}{2}, \\qquad 2^{-2} = \\tfrac{1}{4}, \\qquad 2^{-3} = \\tfrac{1}{8}'),
          warning('A negative exponent does not make the answer negative. It flips the number over. 2⁻³ is one eighth, not −8.', 'The sign trap'),
        ],
      },
      visual: {
        title: 'All five at once',
        blocks: [
          table(['Rule', 'In symbols', 'Why'], [
            ['Product', 'xᵃ · xᵇ = xᵃ⁺ᵇ', 'a factors then b more'],
            ['Quotient', 'xᵃ ÷ xᵇ = xᵃ⁻ᵇ', 'b factors cancel'],
            ['Power of a power', '(xᵃ)ᵇ = xᵃᵇ', 'b lots of a factors'],
            ['Zero', 'x⁰ = 1', 'the pattern forces it'],
            ['Negative', 'x⁻ᵃ = 1/xᵃ', 'the pattern continues'],
          ]),
          tip('If you ever cannot remember which of the first three adds and which multiplies, write out a small case with x³ and x² and count. It takes fifteen seconds and it is never wrong.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Simplify (2x³)² × x⁻⁴.'),
          steps([
            { label: 'The outer power applies to everything inside the bracket.', latex: '(2x^{3})^{2} = 2^{2} \\times x^{6} = 4x^{6}' },
            { label: 'Now multiply by x⁻⁴, adding the exponents.', latex: '4x^{6} \\times x^{-4} = 4x^{6-4}' },
            { label: 'Simplify.', latex: '4x^{2}' },
          ]),
          text('Note the first step: the square applied to the 2 as well as to the x³. A bracket means everything inside it, exactly as it did with multi-step equations.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('exponent-rules', 4)] },
      independent: { title: 'On your own', blocks: [practice('exponent-rules', 6)] },
      challenge: { title: 'Something harder', blocks: [practice('exponent-rules', 3, 'Zero and negative exponents together.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'An exponent counts factors — every rule follows from that.',
            'Multiplying powers of the same base adds the exponents; a power of a power multiplies them.',
            'The base never changes.',
            'x⁰ = 1, forced by the pattern rather than chosen.',
            'A negative exponent gives a reciprocal, not a negative number.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'scientific-notation-01',
    unitId: 'unit-exponent-laws',
    conceptId: 'scientific-notation',
    title: 'Scientific Notation',
    summary: 'Writing the very large and the very small so they can be compared at a glance.',
    objectives: [
      'Write a number in scientific notation, with a coefficient between 1 and 10',
      'Convert back to ordinary form',
      'Compare two numbers written in scientific notation',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'Numbers too big to read',
        blocks: [
          text('The Sun is about 149,600,000,000 metres away. A hydrogen atom is about 0.000000000106 metres across. Neither of those can be read at a glance, and comparing them by counting zeros is a way to make mistakes.'),
          math('1.496 \\times 10^{11} \\qquad 1.06 \\times 10^{-10}'),
          text('**Scientific notation** writes every number as one digit, a decimal part, and a power of ten. The power tells you the size; the digits tell you the precision.'),
        ],
      },
      intuition: {
        title: 'The power counts the places',
        blocks: [
          text('Multiplying by 10 moves the decimal point one place right. Dividing by 10 moves it one place left. That is the entire mechanism.'),
          insight('The coefficient must be at least 1 and less than 10 — exactly one non-zero digit before the point. That is what makes two numbers instantly comparable: whichever has the bigger power of ten is bigger, and you never have to count zeros.'),
          table(['Ordinary', 'Scientific', 'The point moved'], [
            ['4,300', '4.3 × 10³', '3 places left'],
            ['52,000,000', '5.2 × 10⁷', '7 places left'],
            ['0.0067', '6.7 × 10⁻³', '3 places right'],
            ['0.00000091', '9.1 × 10⁻⁷', '7 places right'],
          ]),
          warning('A big number takes a positive power; a small one takes a negative power. Getting the sign backwards turns the distance to the Sun into something smaller than an atom, so check your answer against the size of the original.', 'Which way?'),
          text('One more requirement, easy to miss: 34 × 10³ has the right *value* but is not scientific notation, because 34 is not between 1 and 10. Move the point one more place and raise the power to compensate: 3.4 × 10⁴.'),
        ],
      },
      visual: {
        title: 'Seeing the scale',
        blocks: [
          table(['Thing', 'Size in metres', 'Power'], [
            ['Observable universe', '8.8 × 10²⁶', '26'],
            ['Distance to the Sun', '1.5 × 10¹¹', '11'],
            ['A person', '1.7 × 10⁰', '0'],
            ['A red blood cell', '8 × 10⁻⁶', '−6'],
            ['A hydrogen atom', '1.1 × 10⁻¹⁰', '−10'],
          ]),
          insight('Each step in the last column is a factor of ten. A person is about 10¹⁷ times bigger than an atom — a number you can state precisely because of the notation, and could not otherwise hold in your head at all.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Write 0.00048 in scientific notation, and say which is larger: 3.2 × 10⁵ or 9.7 × 10⁴.'),
          steps([
            { label: 'Put one non-zero digit before the point.', latex: '4.8' },
            { label: 'Count how many places the point must move to rebuild the number.', latex: '4 \\text{ places right}' },
            { label: 'The number is less than 1, so the power is negative.', latex: '4.8 \\times 10^{-4}' },
            { label: 'For the comparison, look at the powers first.', latex: '10^{5} > 10^{4}' },
            { label: 'So the first is larger, whatever the coefficients.', latex: '3.2 \\times 10^{5} = 320{,}000' },
          ]),
          tip('That last point is the payoff. 9.7 looks much bigger than 3.2, and it does not matter — a larger power of ten wins outright.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('scientific-notation', 4)] },
      independent: { title: 'On your own', blocks: [practice('scientific-notation', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('scientific-notation', 3, 'Forms that have the right value but the wrong coefficient.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Scientific notation is a coefficient between 1 and 10, times a power of ten.',
            'The power counts how many places the decimal point moves.',
            'Numbers above 10 take a positive power; numbers below 1 take a negative one.',
            'Compare the powers first — the coefficients only matter if the powers are equal.',
          ]),
        ],
      },
    },
  }),
];
